import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  DEAL_STAGE_TRANSLATIONS,
  FILTER_KEY_TRANSLATIONS,
  LISTING_STAGE_TRANSLATIONS,
} from './data';
import useQueryFilter from 'hooks/useQueryFilter';
import {
  DEAL_STATUS_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
} from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/PortfolioSellerFilters/data';
import PortfolioDefaulEmpty from 'components/PortfolioDefaulEmpty';
import PluscircleIcon from 'icons/Pluscircle.icon';
import {
  BUSINESS_ROUTE,
  CREATE_DEAL_ROUTE,
  PROFILE_ROUTE,
  SELLING_PORTFOLIO_ROUTE,
} from 'data/global';
import { DealStage } from 'models/DealRoom';
import { ListingStage } from 'models/Listing';
import SearchIcon from 'icons/Search.icon';
import SettingsIcon from 'icons/Settings.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';

import './PortfolioDealsEmptyState.styles.scss';
import './PortfolioDealsEmptyState.styles.responsive.scss';

type PortfolioDealsEmptyStateProps = {
  className?: string;
  isBuyerSide?: boolean;
};

const PortfolioDealsEmptyState: React.FC<PortfolioDealsEmptyStateProps> = (
  props,
) => {
  const { className, isBuyerSide } = props;
  const classes = classNames('portfolio-deals-empty-state', className);

  const { activeFilter, hasActiveFilter } = useQueryFilter();

  const { t } = useTranslation();

  const filters = useMemo(
    () => Object.entries(activeFilter) || [],
    [activeFilter],
  );

  const { isActiveOneFilter, firstFilterKey, firstFilterValue } = useMemo(
    () => ({
      isActiveOneFilter: filters.length === 1,
      firstFilterKey: filters[0]?.[0],
      firstFilterValue: filters[0]?.[1],
    }),
    [filters],
  );

  const isActiveStageFilter = useMemo(() => {
    if (!hasActiveFilter) return;

    return (
      isActiveOneFilter &&
      (firstFilterKey === LISTING_STATUS_FILTER_KEY ||
        firstFilterKey === DEAL_STATUS_FILTER_KEY)
    );
  }, [firstFilterKey, isActiveOneFilter, hasActiveFilter]);

  const text = useMemo(() => {
    if (isBuyerSide) return t('portfolioDealsEmptyStateBuyer');

    if (!hasActiveFilter) return t('portfolioDealsEmptyStateSeller');

    if (isActiveOneFilter) {
      const filterKey = FILTER_KEY_TRANSLATIONS[firstFilterKey];
      const dealStageKey =
        DEAL_STAGE_TRANSLATIONS[firstFilterValue as DealStage];
      const listingStageKey =
        LISTING_STAGE_TRANSLATIONS[firstFilterValue as ListingStage];

      if (filterKey) {
        return t('noDealsForSelectedFilter', {
          filter: t(filterKey),
        });
      }

      if (isActiveStageFilter) {
        if (listingStageKey)
          return t('youHaveNoListingStage', {
            listingStage: t(listingStageKey),
          });

        if (dealStageKey)
          return t('youHaveNoDealStage', {
            dealStage: t(dealStageKey),
          });
      }
    }

    return t('noDealsForSelectedCriteria');
  }, [
    isBuyerSide,
    hasActiveFilter,
    isActiveOneFilter,
    firstFilterKey,
    firstFilterValue,
    isActiveStageFilter,
    t,
  ]);

  const actions = useMemo(() => {
    if (isBuyerSide)
      return (
        <Link to={PROFILE_ROUTE} className="ncoded-button ncoded-button--solid">
          {t('setYourPreferences')}
          <SettingsIcon />
        </Link>
      );

    if (isActiveStageFilter)
      return (
        <Link
          to={SELLING_PORTFOLIO_ROUTE}
          className="ncoded-button ncoded-button--outline"
        >
          {t('viewAllDeals')}
          <ArrowrightIcon />
        </Link>
      );

    return (
      <Link
        to={CREATE_DEAL_ROUTE}
        className="ncoded-button ncoded-button--solid"
      >
        {t('newDeal')}
        <PluscircleIcon />
      </Link>
    );
  }, [isBuyerSide, isActiveStageFilter, t]);

  return (
    <PortfolioDefaulEmpty className={classes} text={text}>
      <div className="portfolio-deals-empty-state__actions">
        {actions}

        {!isActiveStageFilter && (
          <Link
            to={BUSINESS_ROUTE}
            className="ncoded-button ncoded-button--outline"
          >
            {t('browseBusinessesForSale')}
            <SearchIcon />
          </Link>
        )}
      </div>
    </PortfolioDefaulEmpty>
  );
};

export default PortfolioDealsEmptyState;
